import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Content,
  ContentBody,
  ContentButton,
  ContentHeader,
  ContentIcon,
  ContentTitle
} from 'components/Content'
import KanBoIcon from 'components/KanBoIcon'
import {
  ToggleModal
} from 'components/Modal'
import AddOfficeDocumentForm from '../AddOfficeDocumentForm'
import styled from 'styled-components'
import AddElementButton from '../AddElementButton'
import ElementContentIcon from '../ContentIcon'
import { useCardContext } from 'dataProviders/Card'
import requestAddDocumentFromTemplate from '../sagas/requestAddDocumentFromTemplate'
import requestAddDocumentToDocumentList from '../sagas/requestAddDocumentToDocumentList'
import { useDispatch } from 'react-redux'
import { useBoardContext } from 'dataProviders/Board'
import { ModalWithBoardContext } from 'components/Modal'
import Documents from 'routes/Board/DocumentList/Documents'
import {
  LeftSidebarView,
  ViewBody,
  BoardModalView,
} from 'components/Sidebar'
import { DocumentTemplatesList, DocumentTemplateItem } from 'routes/Board/DocumentTemplates'
import {
  FormGroup,
  ControlLabel,
} from 'react-bootstrap'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import DocumentItem from 'routes/Board/DocumentList/DocumentItem'

const AddOfficeDocumentContent = styled(Content)`
  background-color: #eceff1;
`

const DOCUMENT = {
  EXISTING: 'existing',
  FROM_TEMPLATE: 'from_template',
  WORD: 'word',
  EXCEL: 'excel',
  POWERPOINT: 'powerpoint',
} as const

type AddDocumentType = typeof DOCUMENT[keyof typeof DOCUMENT]

const DOCUMENTS = [{
  id: DOCUMENT.EXISTING,
  iconName: 'kanbo-24-single-document',
  formattedMessageId: 'CARD_ADD_EXISTING_DOCUMENT',
}, {
  id: DOCUMENT.FROM_TEMPLATE,
  iconName: 'kanbo-24-add-new-document-from-template',
  formattedMessageId: 'DOCUMENT_ADD_FROM_TEMPLATE',
}, {
  id: DOCUMENT.WORD,
  iconName: 'kanbo-24-create-new-word-document',
  formattedMessageId: 'DOCUMENT_WORD_ADD_NEW',
}, {
  id: DOCUMENT.EXCEL,
  iconName: 'kanbo-24-create-new-excel-document',
  formattedMessageId: 'DOCUMENT_EXCEL_ADD_NEW',
}, {
  id: DOCUMENT.POWERPOINT,
  iconName: 'kanbo-24-create-new-powerpoint-document',
  formattedMessageId: 'DOCUMENT_POWERPOINT_ADD_NEW',
}]

type Props = LeftSidebarProps & {
  documentListId: number;
}

const AddDocumentButton: React.FC<Props> = ({
  leftSidebar,
  documentListId,
}) => {
  const dispatch = useDispatch()

  const cardContext = useCardContext()

  const boardContext = useBoardContext()

  const buttonRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  const [addingDocument, setAddingDocument] = useState<boolean | AddDocumentType>(false)

  const showToggleModal = useMemo(() => {
    return addingDocument && addingDocument !== DOCUMENT.EXISTING && addingDocument !== DOCUMENT.FROM_TEMPLATE
  }, [addingDocument])

  useLayoutEffect(() => {
    if (showToggleModal && modalRef.current && buttonRef.current) {
      modalRef.current.style.width = `${buttonRef.current.offsetWidth}px`
    }
  }, [showToggleModal])

  const handleAddItemClick = useCallback(() => {
    setAddingDocument(true)
  }, [])

  const handleAddItemClose = useCallback(() => {
    setAddingDocument(false)
  }, [])

  const handleAddDocumentFromTemplateSubmit = useCallback(({ name, extension, templateSourceId, templateIdentifier }) => {
    if (name) {
      dispatch(requestAddDocumentFromTemplate({
        boardId: boardContext.id,
        cardId: cardContext.id,
        documentListId,
        url: `${name.trim()}.${extension}`,
        templateSourceId,
        templateIdentifier,
      }))

      handleAddItemClose()
    }
  }, [boardContext.id, cardContext.id, documentListId, handleAddItemClose])

  const renderDocument = useCallback((props) => {
    const documentProps = props.type === 'document' ? {
      menu: [{
        icon: <KanBoIcon fixedWidth name='kanbo-16-attach-to-card' />,
        title: <FormattedMessage id='DOCUMENTS_ADD_TO_CARD' />,
        onClick: () => {
          dispatch(requestAddDocumentToDocumentList({
            documentListId,
            url: props.url,
            documentSourceId: props.documentSourceId,
          }))
        }
      }],
      onClick: () => {
        dispatch(requestAddDocumentToDocumentList({
          documentListId,
          url: props.url,
          documentSourceId: props.documentSourceId,
        }))

        handleAddItemClose()
      }
    } : {}

    return (
      <DocumentItem
        {...props}
        {...documentProps}
      />
    )
  }, [documentListId, handleAddItemClose])

  const element = useMemo(() => DOCUMENTS.find(element => element.id === addingDocument), [addingDocument])

  return (
    <>
      <ToggleModal
        backdrop
        show={showToggleModal}
        onToggle={show => {
          if (!show) {
            setAddingDocument(false)
          }
        }}
        attachment='top center'
        targetAttachment='top center'
      >
        <ContentButton
          ref={buttonRef}
          onClick={handleAddItemClick}
        >
          <ContentIcon>
            <KanBoIcon name='kanbo-16-plus' />
          </ContentIcon>
          <ContentTitle>
            <FormattedMessage id='DOCUMENT_LIST_ADD_DOCUMENT' />
          </ContentTitle>
        </ContentButton>
        <div
          ref={modalRef}
        >
          {addingDocument === true ? (
            <Content>
              {DOCUMENTS.map(element =>
                <AddElementButton
                  key={element.id}
                  icon={<KanBoIcon name={element.iconName} />}
                  onClick={() => {
                    setAddingDocument(element.id)
                  }}
                >
                  <FormattedMessage id={element.formattedMessageId} />
                </AddElementButton>
              )}
            </Content>
          ) : (
            <AddOfficeDocumentContent>
              {element &&
                <ContentHeader>
                  <ElementContentIcon>
                    <KanBoIcon name={element.iconName} />
                  </ElementContentIcon>
                  <ContentTitle>
                    <FormattedMessage id={element.formattedMessageId} />
                  </ContentTitle>
                </ContentHeader>
              }
              <ContentBody>
                {addingDocument === DOCUMENT.WORD &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'docx',
                      templateIdentifier: 'word-docx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
                {addingDocument === DOCUMENT.EXCEL &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'xlsx',
                      templateIdentifier: 'xlsx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
                {addingDocument === DOCUMENT.POWERPOINT &&
                  <AddOfficeDocumentForm
                    initialValues={{
                      name: '',
                      extension: 'pptx',
                      templateIdentifier: 'pptx',
                    }}
                    onSubmit={handleAddDocumentFromTemplateSubmit}
                    onCancelClick={handleAddItemClose}
                  />
                }
              </ContentBody>
            </AddOfficeDocumentContent>
          )}
        </div>
      </ToggleModal>
      {addingDocument === DOCUMENT.EXISTING &&
        <ModalWithBoardContext
          boardId={boardContext.id}
          onClose={handleAddItemClose}
        >
          <BoardModalView
            title={<FormattedMessage id='CARD_ADD_EXISTING_DOCUMENT_SELECT_DOCUMENT' />}
          >
            <Documents
              autoSelectDocumentSource
              boardId={boardContext.id}
              documentComponent={renderDocument}
            />
          </BoardModalView>
        </ModalWithBoardContext>
      }
      {addingDocument === DOCUMENT.FROM_TEMPLATE &&
        <ModalWithBoardContext
          boardId={boardContext.id}
          onClose={handleAddItemClose}
        >
          <BoardModalView
            title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
          >
            <DocumentTemplatesList
              elementId={documentListId}
              documentTemplateComponent={(props) =>
                <DocumentTemplateItem
                  {...props}
                  onClick={props.type === 'template' ? () => {
                    leftSidebar.push(() =>
                      <LeftSidebarView
                        title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
                      >
                        <FormGroup>
                          <ViewBody>
                            <ControlLabel><FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE_SELECTED_DOCUMENT' /></ControlLabel>
                          </ViewBody>
                          <DocumentTemplateItem
                            {...props}
                          />
                        </FormGroup>
                        <ViewBody>
                          <AddOfficeDocumentForm
                            initialValues={{
                              name: '',
                              extension: props.extension,
                              templateSourceId: props.documentTemplateSourceId,
                              templateIdentifier: props.identifier
                            }}
                            onSubmit={handleAddDocumentFromTemplateSubmit}
                            onCancelClick={handleAddItemClose}
                          />
                        </ViewBody>
                      </LeftSidebarView>
                    )
                  } : props.onClick}
                />
              }
            />
          </BoardModalView>
        </ModalWithBoardContext>
      }
    </>
  )
}

export default withLeftSidebar(AddDocumentButton)